import React, { useEffect, useState } from "react";

import { Attribute, MdmDeviceDto, MdmObjectDto, useUpdateUserSettingsMutation } from "gql/generated/types";
import { Path } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { TableColumn } from "utils/types";
import useProjectContext from "../hooks/useProjectContext";
import ItemsTable from "./ItemsTable";
import TableColumnSelection from "./TableColumnSelection";
import { errorToaster, successToaster } from "../utils/utils";

type CustomFieldType = Record<
  string,
  {
    value: string | null | undefined;
    id: string | null | undefined;
    title: string | undefined;
  }
>;

type MainTableProps<T> = {
  data: Partial<T>[];
  tableName: "Devices" | "Objects";
  columns: TableColumn<T>[];
  searchFields: Path<T>[];
  loading?: boolean;
};

const MainTable = <T extends MdmDeviceDto | MdmObjectDto>({
  data,
  tableName,
  columns,
  searchFields,
  loading,
}: MainTableProps<T>) => {
  const navigate = useNavigate();
  const { user, userSettings, project } = useProjectContext();
  let columnList: string[] = [];
  let customCol: Attribute[] = [];
  if (tableName === "Devices") {
    columnList = userSettings?.userSettings.deviceTable as string[] ?? [];
    customCol = [
      ...(project?.mdmProjectById.configuration?.deviceConfiguration.customFields ?? []),
    ] as Attribute[];
  } else if (tableName === "Objects") {
    columnList = userSettings?.userSettings.objectTable as string[] ?? [];
    customCol = [
      ...(project?.mdmProjectById.configuration?.objectConfiguration.customFields ?? []),
    ] as Attribute[];
  }
  const mapDefaultColumns =
    columnList.length > 0 ? columnList : [...columns.map(col => col.header), ...columnList];
    
  const [customColumns, setCustomColumns] = useState(mapDefaultColumns);

  useEffect(() => {
    if (userSettings && columnList.length > 0) setCustomColumns(columnList);
  }, [userSettings]);

  const [updateUserSettings, {loading: updateLoading}] = useUpdateUserSettingsMutation({
    onCompleted: () => successToaster("Columns saved successfully"),
    onError: errorToaster,
  });

  const mapCol = [
    ...columns,
    ...(customCol?.map(col => ({
      field: `${col?.id}.value`,
      header: col?.title ?? "",
      sortable: true,
      filter: true,
    })) ?? []),
  ] as TableColumn<T>[];

  const formatObjectFields = data.reduce((acc, obj) => {
    const objFields = obj.customFields?.reduce((accObj, field) => {
      const newObj = {
        value: field?.value?.value,
        id: field?.key,
        title: customCol.find(col => col?.id === field?.key)?.title,
      };
      return {
        ...accObj,
        [field?.key!]: newObj,
      };
    }, {} as CustomFieldType);

    acc.push({
      ...obj,
      ...objFields,
    });

    return acc;
  }, [] as Partial<T>[]);

  const filteredColumns = mapCol.filter(col => customColumns.includes(col.header));

  const handleColumnSave = () => {
    if (tableName === "Devices") {
      updateUserSettings({
        variables: {
          userSettings: {
            id: user?.externalId!,
            deviceTable: customColumns,
          },
        },
      });
      return;
    }
    updateUserSettings({
      variables: {
        userSettings: {
          id: user?.externalId!,
          objectTable: customColumns,
        },
      },
    });
  };

  return (
    <div className="card">
      <TableColumnSelection
        customColumns={customColumns}
        setCustomColumns={setCustomColumns}
        handleColumnSave={handleColumnSave}
        mapCol={mapCol}
        loading={updateLoading}
      />
      <ItemsTable
        columns={filteredColumns}
        data={formatObjectFields as T[]}
        onToolbarBtnClick={() => navigate(tableName === "Devices" ? "new" : "objects/new")}
        searchFields={searchFields}
        toolbarBtnLabel={tableName === "Devices" ? "Add Device" : "Add Object"}
        tableName={tableName}
        isLoading={loading}
      />
    </div>
  );
};

export default MainTable;